import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { SlidersHorizontal, MousePointerClick, Trash2, ExternalLink } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import ConfirmationPopup from './ConfirmationPopup';
import {
  PageHeader,
  Card,
  Segmented,
  SearchInput,
  Pagination,
  Sheet,
  Button,
  Field,
  InfoRow,
  CopyButton,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  inputClass,
  useDebounce,
  PlatformBadge,
  cx
} from './ui';
import { badgeStyleMap, parseClientFromUA, parseDeviceDetails } from '../utils/userAgent';
import { formatDateTime, todayYmd, formatCurrency, formatNumber, formatPercent } from '../utils/format';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UABadge = ({ variant, children, onClick }) => {
  const cls = cx('inline-flex items-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-xs font-medium', badgeStyleMap[variant] || badgeStyleMap.gray);
  return onClick ? <button type="button" onClick={onClick} className={cls}>{children}</button> : <span className={cls}>{children}</span>;
};

const VerifiedBadge = ({ verified }) => (
  <UABadge variant={verified ? 'green' : 'rose'}>{verified ? 'Đã xác thực' : 'Chưa'}</UABadge>
);

// TikTok: verified = cron-job-tiktok đã khớp đơn RioHub về click này; link_status = trạng thái tạo link
const TikTokClickBadge = ({ row }) => {
  if (row.verified) return <UABadge variant="green">Có đơn</UABadge>;
  if (row.link_status === 'failed') return <UABadge variant="rose">Tạo link lỗi</UABadge>;
  if (row.link_status === 'pending') return <UABadge variant="orange">Đang tạo link</UABadge>;
  return <UABadge variant="blue">{row.provider === 'accesstrade' ? 'Link Accesstrade' : 'Đã tạo link'}</UABadge>;
};

const EMPTY_LOCAL = { deviceType: '', client: '', commissionMin: '', commissionMax: '' };

/**
 * Danh sách lượt click (user_orders) của một sàn - Shopee, Lazada, TikTok Shop - hoặc cả 3 sàn (platform = 'all').
 * tabs: phần chọn sàn, hiện ngay dưới tiêu đề
 * config:
 *  - title, endpoint, queryKey
 *  - idFields: [{ param, label }] - các bộ lọc mã phía server (subid, item_id, ...)
 *  - detailFields: [{ key, label, link?, mono? }] - thông tin hiện trong sheet chi tiết
 *  - showProduct: hiện tên sản phẩm trên thẻ
 *  - allowDelete: cho phép chọn/xóa và dọn click rác (chỉ Shopee có API)
 */
const ClickListPage = ({ config, tabs }) => {
  const isAll = config.platform === 'all';
  const isTikTok = config.platform === 'tiktokshop';
  const hasSnapshot = config.platform === 'lazada' || isTikTok;
  // Tab "Tất cả": trạng thái và cách hiện hoa hồng theo sàn của từng dòng
  const statusBadge = (row) => (isTikTok || row.platform === 'tiktokshop')
    ? <TikTokClickBadge row={row} /> : <VerifiedBadge verified={row.verified} />;
  const commissionText = (value, row) => (hasSnapshot || (isAll && row?.platform !== 'shopee'))
    && value != null && value !== '' && Number.isFinite(Number(value))
    ? formatCurrency(value) : (value || '—');
  const queryClient = useQueryClient();
  const [identifier, setIdentifier] = useState('');
  const [dateFrom, setDateFrom] = useState(todayYmd);
  const [dateTo, setDateTo] = useState(todayYmd);
  const [ids, setIds] = useState({});
  const [verified, setVerified] = useState('');
  const [local, setLocal] = useState(EMPTY_LOCAL);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null); // { type: 'selected' | 'cleanup' }
  const [busy, setBusy] = useState(false);

  const debouncedIdentifier = useDebounce(identifier.trim());
  const debouncedIds = useDebounce(ids);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    [config.queryKey, debouncedIdentifier, debouncedIds, dateFrom, dateTo, page, limit],
    async () => {
      const params = { page, limit, platform: config.platform };
      if (debouncedIdentifier) {
        if (UUID_RE.test(debouncedIdentifier)) params.user_id = debouncedIdentifier.toLowerCase();
        else params.email = debouncedIdentifier;
      }
      Object.entries(debouncedIds).forEach(([k, v]) => { if (v) params[k] = v; });
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      return toPage(await api.get(config.endpoint, { params }));
    },
    { keepPreviousData: true, onSuccess: () => setSelected([]),
      refetchInterval: hasSnapshot || isAll ? 15000 : false }
  );

  const all = useMemo(() => data?.items || [], [data]);
  const pagination = data?.pagination || {};

  // Lọc nhanh phía client trên trang hiện tại
  const enriched = useMemo(
    () => all.map((uo) => ({ ...uo, _device: parseDeviceDetails(uo.user_agent, uo.is_pwa), _client: parseClientFromUA(uo.user_agent, uo.is_pwa) })),
    [all]
  );
  const rows = useMemo(() => enriched.filter((uo) => {
    if (verified !== '' && uo.verified !== (verified === 'true')) return false;
    if (local.deviceType && uo._device.deviceType !== local.deviceType) return false;
    if (local.client && uo._client.label !== local.client) return false;
    const comm = parseFloat(uo.commission) || 0;
    if (local.commissionMin !== '' && comm < parseFloat(local.commissionMin)) return false;
    if (local.commissionMax !== '' && comm > parseFloat(local.commissionMax)) return false;
    return true;
  }), [enriched, verified, local]);

  const deviceTypes = useMemo(() => [...new Set(enriched.map((u) => u._device.deviceType))].sort(), [enriched]);
  const clients = useMemo(() => [...new Set(enriched.map((u) => u._client.label))].sort(), [enriched]);
  const verifiedCount = enriched.filter((u) => u.verified).length;
  const advancedCount = Object.values(ids).filter(Boolean).length + Object.values(local).filter((v) => v !== '').length;

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const runDelete = async () => {
    setBusy(true);
    try {
      if (confirm.type === 'cleanup') {
        const res = await api.post('/admin/affiliate-clicks/cleanup');
        toast.success(`Đã xóa ${res.data?.data?.deleted ?? 0} click chưa xác thực > 48h`);
      } else {
        await Promise.all(selected.map((id) => api.delete(`/admin/affiliate-clicks/${id}`)));
        toast.success(`Đã xóa ${selected.length} click`);
        setSelected([]);
      }
      setConfirm(null);
      refetch();
      queryClient.invalidateQueries('dashboard');
    } catch (e) {
      toast.error(errorMessage(e, 'Không thể xóa'));
    } finally {
      setBusy(false);
    }
  };

  const renderCard = (uo) => {
    const checked = selected.includes(uo.id);
    return (
      <Card key={uo.id} className={cx('p-3.5', checked && 'border-gray-900 ring-1 ring-gray-900')}>
        <div className="flex items-start gap-3">
          {config.allowDelete && (
            <input type="checkbox" className="mt-1 h-5 w-5 flex-shrink-0 rounded" checked={checked} onChange={() => toggle(uo.id)} aria-label="Chọn" />
          )}
          <button type="button" onClick={() => setDetail(uo)} className="min-w-0 flex-1 text-left">
            <div className="flex items-start justify-between gap-2">
              <p className="truncate text-sm font-medium text-gray-900">{uo.user?.email || '—'}</p>
              <span className="flex-shrink-0 text-sm font-semibold text-gray-900">{commissionText(uo.commission, uo)}</span>
            </div>
            {config.showProduct && uo.product_name && <p className="mt-0.5 line-clamp-1 text-xs text-gray-600">{uo.product_name}</p>}
            {hasSnapshot && uo.shop_name && <p className="text-xs text-gray-500">{uo.shop_name}</p>}
            {isTikTok && <p className="mt-1 text-xs text-gray-600">Hoàn dự kiến: {uo.cashback == null ? '—' : formatCurrency(uo.cashback)} · #{uo.id}</p>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {isAll && <PlatformBadge platform={uo.platform} />}
              <UABadge variant={uo._device.variant}>{uo._device.deviceType}</UABadge>
              <UABadge variant={uo._client.variant}>{uo._client.label}</UABadge>
              {statusBadge(uo)}
            </div>
            <p className="mt-2 text-xs text-gray-400">{formatDateTime(uo.created_at * 1000)}</p>
          </button>
        </div>
      </Card>
    );
  };

  const desktopTable = (
    <Card className="hidden overflow-hidden md:block">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {config.allowDelete && (
                <th className="w-10 px-4 py-2.5">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={rows.length > 0 && rows.every((r) => selected.includes(r.id))}
                    onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.id) : [])}
                    aria-label="Chọn tất cả"
                  />
                </th>
              )}
              {['Người dùng', ...(isAll ? ['Sàn'] : []), 'Hoa hồng', ...(isTikTok ? ['Hoàn dự kiến'] : []), ...(config.showProduct ? ['Sản phẩm'] : []), 'Thiết bị', 'Mở bằng', isTikTok ? 'Trạng thái' : 'Xác thực', 'Thời gian'].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium text-gray-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((uo) => (
              <tr key={uo.id} className="hover:bg-gray-50">
                {config.allowDelete && (
                  <td className="px-4 py-2.5"><input type="checkbox" className="h-4 w-4" checked={selected.includes(uo.id)} onChange={() => toggle(uo.id)} /></td>
                )}
                <td className="px-4 py-2.5">
                  <button type="button" className="text-left font-medium text-gray-900 hover:text-gray-600" onClick={() => setDetail(uo)}>{uo.user?.email || '—'}</button>
                </td>
                {isAll && <td className="px-4 py-2.5"><PlatformBadge platform={uo.platform} /></td>}
                <td className="px-4 py-2.5 font-medium">{commissionText(uo.commission, uo)}</td>
                {isTikTok && <td className="px-4 py-2.5 font-medium">{uo.cashback == null ? '—' : formatCurrency(uo.cashback)}</td>}
                {config.showProduct && <td className="max-w-xs px-4 py-2.5"><span className="line-clamp-2 text-xs text-gray-600">{uo.product_name || '—'}</span></td>}
                <td className="px-4 py-2.5"><UABadge variant={uo._device.variant} onClick={() => setDetail(uo)}>{uo._device.deviceType}</UABadge></td>
                <td className="px-4 py-2.5"><UABadge variant={uo._client.variant}>{uo._client.label}</UABadge></td>
                <td className="px-4 py-2.5">{statusBadge(uo)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-600">{formatDateTime(uo.created_at * 1000)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  return (
    <div>
      <PageHeader
        title={config.title}
        subtitle={`${pagination.total ?? 0} lượt click · trang này ${rows.length}/${all.length}${rows.length !== all.length ? ' (đã lọc)' : ''}`}
        actions={config.allowDelete && (
          <>
            {selected.length > 0 && (
              <Button variant="danger" size="sm" onClick={() => setConfirm({ type: 'selected' })}>
                <Trash2 className="h-4 w-4" /> Xóa {selected.length} đã chọn
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-red-600" onClick={() => setConfirm({ type: 'cleanup' })}>
              <Trash2 className="h-4 w-4" /> Dọn click chưa xác thực &gt; 48h
            </Button>
          </>
        )}
      />

      {tabs}

      <div className="filter-bar">
        <div className="flex gap-2 lg:min-w-[18rem] lg:flex-1">
          <SearchInput className="flex-1" value={identifier} onChange={(v) => { setIdentifier(v); setPage(1); }} placeholder="Email hoặc user ID" />
          <Button variant={advancedCount ? 'soft' : 'outline'} size="icon" className="relative h-11 w-11 flex-shrink-0 md:h-9 md:w-9" onClick={() => setShowFilters(true)} aria-label="Bộ lọc">
            <SlidersHorizontal className="h-4 w-4" />
            {advancedCount > 0 && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-[11px] text-white">{advancedCount}</span>}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:w-80">
          <Field label="Từ ngày"><input type="date" className={inputClass} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} /></Field>
          <Field label="Đến ngày"><input type="date" className={inputClass} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} /></Field>
        </div>
        {!isTikTok && <Segmented
          value={verified}
          onChange={setVerified}
          options={[
            { value: '', label: 'Tất cả', count: all.length },
            { value: 'true', label: 'Đã xác thực', count: verifiedCount },
            { value: 'false', label: 'Chưa xác thực', count: all.length - verifiedCount }
          ]}
        />}
      </div>

      <ErrorBanner message={error ? (errorMessage(error, 'Không thể tải danh sách')) : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : rows.length === 0 ? (
        <EmptyState icon={MousePointerClick} title="Không có lượt click" description="Thử đổi khoảng ngày hoặc bộ lọc." />
      ) : (
        <>
          <div className="space-y-3 md:hidden">{rows.map(renderCard)}</div>
          {desktopTable}
        </>
      )}

      {pagination.total > 0 && (
        <Pagination
          page={page}
          totalPages={pagination.pages}
          total={pagination.total}
          onChange={setPage}
          limit={limit}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
          disabled={isFetching}
        />
      )}

      {/* Bộ lọc nâng cao */}
      <Sheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title="Bộ lọc"
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => { setIds({}); setLocal(EMPTY_LOCAL); setPage(1); }}>Đặt lại</Button>
            <Button onClick={() => setShowFilters(false)}>Xong</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {config.idFields.map((f) => (
              <Field key={f.param} label={f.label}>
                <input className={inputClass} value={ids[f.param] || ''} onChange={(e) => { setIds((prev) => ({ ...prev, [f.param]: e.target.value.trim() })); setPage(1); }} />
              </Field>
            ))}
          </div>
          <p className="text-xs font-medium text-gray-500">Lọc nhanh trên trang hiện tại</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Thiết bị">
              <select className={inputClass} value={local.deviceType} onChange={(e) => setLocal((l) => ({ ...l, deviceType: e.target.value }))}>
                <option value="">Tất cả</option>
                {deviceTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Mở bằng">
              <select className={inputClass} value={local.client} onChange={(e) => setLocal((l) => ({ ...l, client: e.target.value }))}>
                <option value="">Tất cả</option>
                {clients.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Hoa hồng từ">
              <input type="number" inputMode="decimal" className={inputClass} value={local.commissionMin} onChange={(e) => setLocal((l) => ({ ...l, commissionMin: e.target.value }))} />
            </Field>
            <Field label="Hoa hồng đến">
              <input type="number" inputMode="decimal" className={inputClass} value={local.commissionMax} onChange={(e) => setLocal((l) => ({ ...l, commissionMax: e.target.value }))} />
            </Field>
          </div>
        </div>
      </Sheet>

      {/* Chi tiết click */}
      <Sheet open={!!detail} onClose={() => setDetail(null)} title="Chi tiết lượt click">
        {detail && (
          <div className="space-y-4">
            {hasSnapshot && (
              <Card className="space-y-3 p-4">
                {detail.image_url && <img src={detail.image_url} alt={detail.product_name || 'Sản phẩm'} className="h-32 w-32 rounded-xl object-contain" loading="lazy" referrerPolicy="no-referrer" />}
                <p className="font-semibold text-gray-900">{detail.product_name || 'Chưa có thông tin sản phẩm'}</p>
                <div className="divide-y divide-gray-100">
                  <InfoRow label="Giá sản phẩm">{detail.price == null ? '—' : formatCurrency(detail.price)}</InfoRow>
                  {isTikTok && <InfoRow label="Giá cao nhất">{detail.price_max == null ? '—' : formatCurrency(detail.price_max)}</InfoRow>}
                  <InfoRow label="Giá gốc">{detail.original_price == null ? '—' : formatCurrency(detail.original_price)}</InfoRow>
                  <InfoRow label="Tỷ lệ hoa hồng">{detail.commission_rate == null ? '—' : formatPercent(detail.commission_rate, 2)}</InfoRow>
                  {isTikTok && <>
                    <InfoRow label="Tiền tệ">{detail.currency || '—'}</InfoRow>
                    <InfoRow label="Tỷ lệ chia hoàn tiền">{detail.cashback_percent == null ? '—' : formatPercent(detail.cashback_percent / 100, 2)}</InfoRow>
                    <InfoRow label="Hoàn tiền dự kiến">{detail.cashback == null ? '—' : formatCurrency(detail.cashback)}</InfoRow>
                  </>}
                  <InfoRow label="Đã bán">{detail.sales == null ? '—' : formatNumber(detail.sales)}</InfoRow>
                  <InfoRow label="Thông tin lấy lúc">{formatDateTime(detail.product_info_at)}</InfoRow>
                </div>
                <p className="text-xs text-gray-500">{isTikTok ? 'Thông tin tại thời điểm tạo link. Chưa xác nhận phát sinh đơn hàng hoặc tiền hoàn được duyệt.' : 'Hoa hồng dự kiến từ Lazada tại thời điểm lấy thông tin; chưa phải tiền hoàn cho người dùng.'}</p>
              </Card>
            )}
            <Card className="divide-y divide-gray-100 px-4">
              <InfoRow label="Người dùng">{detail.user?.email || '—'}</InfoRow>
              {isAll && <InfoRow label="Sàn"><PlatformBadge platform={detail.platform} /></InfoRow>}
              <InfoRow label="Hoa hồng">{commissionText(detail.commission, detail)}</InfoRow>
              <InfoRow label="Thời gian">{formatDateTime(detail.created_at * 1000)}</InfoRow>
              <InfoRow label={isTikTok ? 'Trạng thái' : 'Xác thực'}>{statusBadge(detail)}</InfoRow>
              {config.detailFields.map((f) => {
                const value = detail[f.key];
                if (!value) return <InfoRow key={f.key} label={f.label}>—</InfoRow>;
                return (
                  <InfoRow key={f.key} label={f.label} mono={f.mono}>
                    <span className="inline-flex items-center gap-1">
                      {f.link ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gray-900 underline underline-offset-2">
                          Mở <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <span className="break-all">{value}</span>
                      )}
                      <CopyButton value={value} />
                    </span>
                  </InfoRow>
                );
              })}
            </Card>
            <Card className="divide-y divide-gray-100 px-4">
              <InfoRow label="Loại thiết bị">{detail._device.deviceType}</InfoRow>
              {isTikTok && <InfoRow label="PWA">{detail.is_pwa == null ? 'Chưa ghi nhận' : detail.is_pwa ? 'Có' : 'Không'}</InfoRow>}
              <InfoRow label="Thiết bị">{detail._device.deviceName || 'Không rõ'}</InfoRow>
              <InfoRow label="Hệ điều hành">{detail._device.os || 'Không rõ'}</InfoRow>
              <InfoRow label="Mở bằng">{detail._client.label}</InfoRow>
            </Card>
            {isTikTok && ['tracking_params', 'product_snapshot', 'provider_snapshot'].map(key => (
              <details key={key} className="rounded-xl border p-3">
                <summary className="cursor-pointer text-sm font-medium">{{ tracking_params: 'Tham số tracking', product_snapshot: 'Thông tin sản phẩm đã lưu', provider_snapshot: 'Dữ liệu sản phẩm từ Accesstrade' }[key]}</summary>
                <pre className="mt-2 whitespace-pre-wrap break-all text-xs">{detail[key] ? JSON.stringify(detail[key], null, 2) : 'Chưa ghi nhận'}</pre>
              </details>
            ))}
            <div>
              <p className="mb-1 text-xs font-medium text-gray-500">User Agent</p>
              <p className="break-all rounded-xl bg-gray-50 p-3 font-mono text-xs text-gray-700">{detail.user_agent || 'Không có'}</p>
            </div>
          </div>
        )}
      </Sheet>

      <ConfirmationPopup
        isOpen={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={runDelete}
        title={confirm?.type === 'cleanup' ? 'Dọn click rác' : 'Xóa click đã chọn'}
        message={confirm?.type === 'cleanup'
          ? 'Xóa toàn bộ lượt click chưa xác thực tạo quá 48 giờ? Không thể hoàn tác.'
          : `Xóa ${selected.length} lượt click đã chọn? Không thể hoàn tác.`}
        confirmText="Xóa"
        type="danger"
        isLoading={busy}
      />
    </div>
  );
};

export default ClickListPage;
